import React, { useState } from 'react';
import { Plus, Minus, HelpCircle, Clock, Brain, Target, Globe, Settings } from 'lucide-react';

// FAQ data (icons added in render). Used for both UI and FAQPage schema.
const faqItemsData = [
  {
    id: 1,
    question: "What does Kolanut do in plain terms?",
    answer: "Kolanut helps you find ad dollars you are wasting before you spend more.\n\n• Connect your POS, online store, and Meta/Google accounts\n• Scan your last 30 days of spend and revenue data\n• Flag campaigns that are losing money\n• Show clear actions you can take immediately",
  },
  {
    id: 2,
    question: "Which tools can I connect?",
    answer: "You can connect the tools you already use.\n\n• POS systems like Square\n• Online stores like Shopify\n• Booking/CRM tools like Boulevard\n• Ad channels like Meta Ads and Google Ads",
  },
  {
    id: 3,
    question: "Will Kolanut run changes automatically?",
    answer: "No. Kolanut does not auto-execute changes in your ad accounts.\n\n• You get a report with clear recommendations\n• You choose what to pause, fix, or scale\n• You stay in full control of execution",
  },
  {
    id: 4,
    question: "How does Kolanut decide what is waste?",
    answer: "Kolanut compares spend to results and highlights poor-performing campaigns.\n\n• Looks at campaign-level and channel-level performance\n• Compares cost against tracked outcomes and revenue\n• Surfaces low-ROAS campaigns and avoidable spend\n• Prioritizes the first actions with biggest expected impact",
  },
  {
    id: 5,
    question: "How long does setup take?",
    answer: "Most teams can connect accounts and get a first report in minutes.\n\n• No code setup\n• No CSV uploads\n• Simple guided connect flow",
  },
  {
    id: 6,
    question: "Who is this best for?",
    answer: "Kolanut is built for SMB teams that spend on ads and want clearer decisions.\n\n• Founders and operators managing growth themselves\n• Marketing teams that need faster spend audits\n• Businesses using Meta/Google plus POS or store data",
  },
];

const faqIcons = [Target, Globe, Settings, Brain, Clock, HelpCircle];

const FAQSection = () => {
  const [expandedIndex, setExpandedIndex] = useState(null);

  const toggleExpand = (index) => {
    setExpandedIndex(prevIndex => {
      if (prevIndex === index) {
        return null;
      } else {
        return index;
      }
    });
  };

  const faqItems = faqItemsData.map((item, index) => {
    const IconComponent = faqIcons[index] || HelpCircle;
    return { ...item, icon: <IconComponent /> };
  });

  const faqPageSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: faqItemsData.map((item) => ({
      "@type": "Question",
      name: item.question,
      acceptedAnswer: {
        "@type": "Answer",
        text: item.answer.replace(/\n\n•/g, " •").replace(/\n/g, " "),
      },
    })),
  };

  return (
    <section id="faq" className="py-12 md:py-20 bg-gray-100">
      <div className="max-w-6xl mx-auto px-4 md:px-6">
        <div className="text-center mb-10 md:mb-14 max-w-2xl mx-auto px-4">
          <p className="text-xs sm:text-sm font-semibold uppercase tracking-[0.2em] text-black/80 mb-6">
            Frequently asked questions
          </p>
          <h2 className="text-2xl sm:text-3xl md:text-[2.125rem] font-semibold text-black tracking-tight leading-snug mb-4">
            Questions before you connect?
          </h2>
          <p className="text-base sm:text-lg text-gray-600 leading-relaxed">
            Clear answers on setup, control, and how Kolanut finds wasted ad spend.
          </p>
        </div>
        
        {/* FAQ tile grid - responsive layout */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5 md:gap-6 items-start">
          {faqItems.map((item, index) => (
            <div 
              key={`faq-${item.id}-${index}`}
              className="bg-white border border-gray-100 rounded-xl shadow-md overflow-hidden transition-all duration-300 hover:shadow-lg self-start"
            >
              {/* Question header with toggle button */}
              <div 
                className="p-5 md:p-6 flex items-center justify-between cursor-pointer"
                onClick={() => toggleExpand(index)}
              >
                <div className="flex items-center pr-4">
                  <div className="bg-[#c4ffa0]/10 rounded-full p-2 mr-3 text-black">
                    {React.cloneElement(item.icon, { className: 'w-5 h-5 text-black' })}
                  </div>
                  <h3 className="font-semibold text-base md:text-lg text-black">{item.question}</h3>
                </div>
                <button 
                  className={`flex-shrink-0 p-1.5 rounded-full transition-colors ${
                    expandedIndex === index 
                      ? 'bg-[#c4ffa0]/20 text-black' 
                      : 'bg-gray-100 text-gray-500 hover:bg-gray-200'
                  }`}
                  aria-label={expandedIndex === index ? "Collapse" : "Expand"}
                >
                  {expandedIndex === index ? <Minus size={18} /> : <Plus size={18} />}
                </button>
              </div>
              
              {/* Expandable answer section */}
              {expandedIndex === index && (
                <div className="border-t border-gray-100">
                  <div className="p-5 md:p-6">
                    {item.answer.split('\n\n').map((paragraph, i) => (
                      <React.Fragment key={i}>
                        {paragraph.startsWith('•') ? (
                          <ul className="list-disc pl-5 mb-3 space-y-1">
                            {paragraph.split('\n').map((bulletPoint, j) => (
                              <li key={j} className="text-gray-600">
                                {bulletPoint.replace('• ', '')}
                              </li>
                            ))}
                          </ul>
                        ) : (
                          <p className="text-gray-600 mb-3">{paragraph}</p>
                        )}
                      </React.Fragment>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* FAQPage schema for rich results and AEO */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(faqPageSchema) }}
      />

      <style jsx>{`
        @keyframes fadeIn {
          from {
            opacity: 0;
            transform: translateY(10px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
      `}</style>
    </section>
  );
};

export default FAQSection;
